// @ts-nocheck
import { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import { Notification } from "@snapchat/snap-design-system";

import { useQuery } from "@apollo/client";
import { allChangesHaveBeenSaved, changesHaveBeenMade, groupDetailsError } from "../../common/model";
import { useAddOrUpdateConnections, useRemoveConnections } from "../../connections/hooks";
import { GroupStatus } from "../../../gql-generated/types";
import { type LensListItemModel } from "../../lenses/models";
import { getGroupDetails } from "../api/queries/getGroupV2";
import { type GetGroupDetailsQuery, type GetGroupDetailsQueryVariables } from "../api/queries/getGroupV2.gql-generated";
import { GroupDetailsModelV2, type LensUpdateData } from "../group-details/models";
import { groupDetailsReducerV2 } from "../group-details/reducers";

import { useSaveGroup } from "./useSaveGroup";

export function useGroupDetails() {
    const [saving, setSaving] = useState(false);
    const { groupId } = useParams();
    const notificationApi = useContext(Notification.Context);
    const [groupDetails, dispatch] = useReducer(groupDetailsReducerV2, new GroupDetailsModelV2());
    const { orgId } = useParams();

    const {
        loading,
        error,
        data: groupData,
    } = useQuery<GetGroupDetailsQuery, GetGroupDetailsQueryVariables>(getGroupDetails, {
        variables: { id: groupId, organizationId: orgId },
        errorPolicy: "all",
    });
    const { saveGroup, error: saveError } = useSaveGroup();
    const { addOrUpdateConnections, error: updateError } = useAddOrUpdateConnections();
    const { removeGroupConnections, error: removeError } = useRemoveConnections();

    useEffect(() => {
        if (groupData && !saving) {
            dispatch({ type: "initialize", group: groupData.getGroupV2 });
        }
    }, [groupData, saving]);

    const displayNotifications = useCallback(() => {
        const errors = [saveError, updateError, removeError].filter((err) => err != null);
        if (!errors.length) {
            notificationApi.success(allChangesHaveBeenSaved);
        }

        if (Boolean(errors.length)) {
            const defaultError = "An error occurred while updating this group. Please try again.";
            errors.map((err, index) => {
                const message = Boolean(err?.message.length) ? err.message : defaultError;
                notificationApi.error(groupDetailsError(message, index));
            });
        }
    }, [saveError, updateError, removeError, notificationApi]);

    const saveChanges = useCallback(async () => {
        setSaving(true);

        try {
            if (groupDetails.info.isChanged) {
                await saveGroup({
                    id: groupDetails.info.id,
                    name: groupDetails.info.name,
                    status: GroupStatus.Active,
                    description: groupDetails.info.description,
                }).catch(console.info);
            }

            await removeGroupConnections(groupId, groupDetails.removedLensIds);
            await addOrUpdateConnections(groupId, groupDetails.newOrUpdatedConnections);
        } finally {
            setSaving(false);
        }

        displayNotifications();
    }, [addOrUpdateConnections, displayNotifications, groupDetails, removeGroupConnections, saveGroup, groupId]);

    const addLensesToGroup = useCallback(
        (lenses: LensListItemModel[]) => {
            dispatch({ type: "addLenses", lenses });
            notificationApi.info(changesHaveBeenMade);
        },
        [notificationApi],
    );

    const removeLensFromGroup = useCallback(
        (lensId: string) => {
            dispatch({ type: "removeLens", lensId });
            notificationApi.info(changesHaveBeenMade);
        },
        [notificationApi],
    );

    const updateFilter = useCallback((searchValue: string) => {
        dispatch({ type: "filter", lensName: searchValue });
    }, []);

    const updateLensInGroup = useCallback(
        (updatedLens: LensUpdateData) => {
            dispatch({ type: "changeLens", updatedLens });
            notificationApi.info(changesHaveBeenMade);
        },
        [notificationApi],
    );

    const updateName = useCallback(
        (newName: string) => {
            dispatch({ type: "changeName", name: newName });
            notificationApi.info(changesHaveBeenMade);
        },
        [notificationApi],
    );

    const updateDescription = useCallback(
        (newDescription: string) => {
            dispatch({ type: "changeDescription", description: newDescription });
            notificationApi.info(changesHaveBeenMade);
        },
        [notificationApi],
    );

    return {
        loading,
        saving,
        error,
        saveChanges,
        addLensesToGroup,
        removeLensFromGroup,
        updateFilter,
        updateLensInGroup,
        groupDetails,
        updateName,
        updateDescription,
    };
}
